import {memo, useCallback} from 'react'

import {shortcutFromEvent, SHORTCUTS} from '../../helpers/keyboard-shortcuts'
import {BaseProjectViewFilterInput} from '../filter-bar/base-project-view-filter-input'
import {useBoardContext} from './board-context'
import {useOmnibarVisibility} from './hooks/use-omnibar-visibility'
import {
  focusCard,
  focusPrevious,
  focusSearchInput,
  isSearchInputFocus,
  useBoardNavigation,
  useStableBoardNavigation,
} from './navigation'

export const BoardFilterInput = memo(function BoardFilterInput() {
  const {navigationDispatch} = useStableBoardNavigation()

  const {
    state: {focus, previousFocus},
  } = useBoardNavigation()

  const dispatchInputFocus = useCallback(() => {
    navigationDispatch(focusSearchInput())
  }, [navigationDispatch])

  const {groupedItems, groupByFieldOptions} = useBoardContext()
  const {enableOmnibar} = useOmnibarVisibility()

  const updateFocus = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (previousFocus && shortcutFromEvent(e) === SHORTCUTS.ESCAPE) {
        navigationDispatch(focusPrevious())
        return
      }

      for (const [horizontalGroupIndex, horizontalGroup] of groupedItems.horizontalGroups.entries()) {
        if (horizontalGroup.isCollapsed) continue

        for (const [verticalGroupIndex, verticalGroup] of groupByFieldOptions.entries()) {
          const currentVerticalGroup = horizontalGroup.itemsByVerticalGroup[verticalGroup.id]?.items
          if (currentVerticalGroup && currentVerticalGroup.length > 0) {
            const id = currentVerticalGroup[0]?.id
            if (!id) return
            navigationDispatch(focusCard(horizontalGroupIndex, verticalGroupIndex, id))
            return
          }
        }
      }

      enableOmnibar()
      e.preventDefault()
      e.stopPropagation()
    },
    [previousFocus, enableOmnibar, navigationDispatch, groupedItems.horizontalGroups, groupByFieldOptions],
  )

  const onInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLButtonElement>): void => {
      switch (shortcutFromEvent(e)) {
        case SHORTCUTS.ESCAPE:
          updateFocus(e)
          return
        case SHORTCUTS.ARROW_DOWN: {
          updateFocus(e)
          return
        }
      }
    },
    [updateFocus],
  )

  return (
    <BaseProjectViewFilterInput
      onInputKeyDown={onInputKeyDown}
      dispatchInputFocusEvent={dispatchInputFocus}
      isFocused={isSearchInputFocus(focus)}
    />
  )
})
